import { DateTime } from 'luxon';
import { toStatusKey } from '../status/displayStatus';
export const DATE_RANGE_LABELS = {
    'last-90-days': 'Last 90 days',
    'last-365-days': 'Last 365 days',
    'this-month': 'This month',
    'this-week': 'This week',
    'last-3-months': 'Last 3 months',
    'this-year': 'This year'
};
// Ranges surfaced in the dashboard dropdown. Other keys remain supported in
// `dateRangeStart()` for backward-compat with persisted state but aren't
// user-facing per v7 feedback.
export const VISIBLE_DATE_RANGE_KEYS = [
    'last-90-days',
    'last-365-days'
];
export const DEFAULT_FILTERS = {
    search: '',
    statusKeys: [],
    dateRange: 'last-90-days',
    sortField: 'date',
    sortDirection: 'desc'
};
// Inclusive start boundary for a canned range, computed with Luxon so week/
// month/year edges respect the local zone. The range is [start, now].
export const dateRangeStart = (range, now) => {
    switch (range) {
        case 'last-90-days':
            return now.minus({ days: 90 }).startOf('day');
        case 'last-365-days':
            return now.minus({ days: 365 }).startOf('day');
        case 'this-week':
            return now.startOf('week');
        case 'this-month':
            return now.startOf('month');
        case 'last-3-months':
            return now.minus({ months: 3 }).startOf('day');
        case 'this-year':
            return now.startOf('year');
    }
};
// A missing or unparseable eventDate is treated as "in range" so backend
// responses that omit the field (e.g. minimal list payloads) still surface
// in the dashboard rather than being silently hidden.
const withinRange = (eventDate, range, now) => {
    if (!eventDate) {
        return true;
    }
    const parsed = DateTime.fromISO(eventDate);
    if (!parsed.isValid) {
        return true;
    }
    return parsed >= dateRangeStart(range, now) && parsed <= now;
};
// Substring match over the three fields v3 surfaces in the typeahead UI:
// chargeback PSP, original PSP, reservation ID. Other identifiers stay
// searchable via the case details page.
const SEARCHABLE_FIELDS = [
    item => item.pspReference,
    item => item.originalReference,
    item => item.reservationId
];
const matchesSearch = (item, search) => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) {
        return true;
    }
    return SEARCHABLE_FIELDS.some(get => {
        const value = get(item);
        return typeof value === 'string' && value.toLowerCase().includes(term);
    });
};
const matchesStatus = (item, statusKeys) => {
    if (statusKeys.length === 0) {
        return true;
    }
    return statusKeys.includes(toStatusKey(item.defenseStatus, item.defenseResult));
};
const toMillis = (iso) => {
    if (!iso) {
        return 0;
    }
    const millis = DateTime.fromISO(iso).toMillis();
    return Number.isNaN(millis) ? 0 : millis;
};
const compareCases = (a, b, field) => {
    if (field === 'amount') {
        return (a.amount ?? 0) - (b.amount ?? 0);
    }
    if (field === 'defenseEndDate') {
        return toMillis(a.defensePeriodEndsAt) - toMillis(b.defensePeriodEndsAt);
    }
    return toMillis(a.eventDate) - toMillis(b.eventDate);
};
// Apply search + status + date filters then sort. Pure: `now` is injected so
// the canned ranges are deterministic and testable.
export const filterAndSortCases = (cases, filters, now) => {
    const filtered = cases.filter(item => matchesSearch(item, filters.search) &&
        matchesStatus(item, filters.statusKeys) &&
        withinRange(item.eventDate, filters.dateRange, now));
    const sorted = [...filtered].sort((a, b) => compareCases(a, b, filters.sortField));
    return filters.sortDirection === 'desc' ? sorted.reverse() : sorted;
};
export const buildSearchSuggestions = (cases, search, limit = 5) => {
    const term = search.trim().toLowerCase();
    if (term.length === 0) {
        return [];
    }
    const out = [];
    for (const item of cases) {
        if (out.length >= limit) {
            break;
        }
        if (item.pspReference.toLowerCase().includes(term)) {
            out.push({
                pspReference: item.pspReference,
                field: 'pspReference',
                value: item.pspReference
            });
            continue;
        }
        if (item.originalReference?.toLowerCase().includes(term)) {
            out.push({
                pspReference: item.pspReference,
                field: 'originalReference',
                value: item.originalReference
            });
            continue;
        }
        if (item.reservationId?.toLowerCase().includes(term)) {
            out.push({
                pspReference: item.pspReference,
                field: 'reservationId',
                value: item.reservationId
            });
        }
    }
    return out;
};
